import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  IconButton,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  LocalGasStation as GasIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccountBalanceWallet as WalletIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from '../../request/http-common';

const WithdrawalVerificationModal = ({ open, onClose, onComplete }) => {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [completedSteps, setCompletedSteps] = useState({
    verification: false,
    gasFee: false
  });

  useEffect(() => {
    if (open) {
      fetchRequirements();
    }
  }, [open]);

  const fetchRequirements = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/withdrawal-requirements');
      
      if (response.data.success) {
        setRequirements(response.data.data);
        
        // Determine current step based on completed requirements
        if (response.data.data.can_withdraw) {
          setCurrentStep(-1); // All complete
        } else if (response.data.data.verification_completed && response.data.data.verification_required) {
          setCurrentStep(1); // Move to gas fee
        } else {
          setCurrentStep(0); // Start with verification
        }

        setCompletedSteps({
          verification: response.data.data.verification_completed,
          gasFee: response.data.data.gas_fee_completed
        });
      }
    } catch (error) {
      showAlert('error', 'Error fetching withdrawal requirements');
      console.error('Error fetching requirements:', error);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const handleTokenVerification = async (paymentMethod = 'wallet_balance') => {
    try {
      setProcessing(true);
      const response = await axios.post('/api/process-token-verification', {
        payment_method: paymentMethod
      });

      if (response.data.success) {
        if (paymentMethod === 'wallet_balance') {
          showAlert('success', 'Token verification completed successfully!');
          setCompletedSteps(prev => ({ ...prev, verification: true }));
          
          // Refresh requirements to get updated status
          setTimeout(() => {
            fetchRequirements();
          }, 1000);
          
          // Check if gas fee is also required
          if (!requirements.gas_fee_required) {
            // No gas fee required, close modal and complete
            setTimeout(() => {
              onComplete();
              onClose();
            }, 2000);
          } else {
            showAlert('info', 'Verification complete! Gas fee payment is now required.');
          }
        } else {
          showAlert('info', 'External payment initiated. Please complete the payment.');
        }
        
        fetchRequirements(); // Refresh requirements
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Error processing verification');
    } finally {
      setProcessing(false);
    }
  };

  const handleGasFeePayment = async (paymentMethod = 'wallet_balance') => {
    try {
      setProcessing(true);
      const response = await axios.post('/api/process-gas-fee-payment', {
        payment_method: paymentMethod
      });

      if (response.data.success) {
        if (paymentMethod === 'wallet_balance') {
          showAlert('success', 'Gas fee payment completed successfully!');
          setCompletedSteps(prev => ({ ...prev, gasFee: true }));
          setCurrentStep(-1);
          
          setTimeout(() => {
            onComplete();
            onClose();
          }, 2000);
        } else {
          showAlert('info', 'External payment initiated. Please complete the payment.');
        }
        
        fetchRequirements(); // Refresh requirements
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Error processing gas fee');
    } finally {
      setProcessing(false);
    }
  };

  const getStepIcon = (step, completed) => {
    if (completed) return <CheckIcon color="success" />;
    if (step === 0) return <SecurityIcon color="primary" />;
    if (step === 1) return <GasIcon color="primary" />;
    return <InfoIcon />;
  };

  const formatAmount = (amount, tokenType) => {
    return `${parseFloat(amount).toFixed(2)} ${tokenType}`;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!requirements || requirements.can_withdraw) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
        <DialogContent>
          <Box textAlign="center" py={4}>
            <CheckIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              All Requirements Complete!
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You can proceed with your withdrawal.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained" fullWidth>
            Continue with Withdrawal
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const steps = [];
  
  // Only show verification step if it's required and not completed
  if (requirements.verification_required && !completedSteps.verification) {
    steps.push({
      label: 'Token Verification',
      description: `Pay ${formatAmount(requirements.verification_amount, requirements.verification_token_type)} for verification`,
      completed: completedSteps.verification,
      action: handleTokenVerification,
      amount: requirements.verification_amount,
      tokenType: requirements.verification_token_type,
      icon: <SecurityIcon />
    });
  }

  // Only show gas fee step if verification is complete (or not required) and gas fee is required but not completed
  if ((!requirements.verification_required || completedSteps.verification) && 
      requirements.gas_fee_required && !completedSteps.gasFee) {
    steps.push({
      label: 'Gas Fee Payment',
      description: `Pay ${formatAmount(requirements.gas_fee_amount, requirements.gas_fee_token_type)} for gas fees`,
      completed: completedSteps.gasFee,
      action: handleGasFeePayment,
      amount: requirements.gas_fee_amount,
      tokenType: requirements.gas_fee_token_type,
      icon: <GasIcon />
    });
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Withdrawal Requirements</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {alert.show && (
          <Alert severity={alert.type} sx={{ mb: 3 }}>
            {alert.message}
          </Alert>
        )}

        {processing && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
            <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
              Processing payment...
            </Typography>
          </Box>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {steps.length > 0 ? 
              `Complete the ${steps[0].label.toLowerCase()} to continue with your withdrawal. Requirements must be completed in order.` :
              'All withdrawal requirements have been completed!'
            }
          </Typography>
        </Alert>

        <Stepper activeStep={currentStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={index} completed={step.completed}>
              <StepLabel
                icon={getStepIcon(index, step.completed)}
                sx={{ 
                  '& .MuiStepLabel-label': { 
                    fontWeight: step.completed ? 'bold' : 'normal',
                    color: step.completed ? 'success.main' : 'inherit'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.label}
                  {step.completed && <Chip label="Completed" size="small" color="success" />}
                </Box>
              </StepLabel>
              <StepContent>
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <Typography variant="body1" gutterBottom>
                          {step.description}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Amount Required: <strong>{formatAmount(step.amount, step.tokenType)}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {!step.completed && (
                            <>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<WalletIcon />}
                                onClick={() => step.action('wallet_balance')}
                                disabled={processing}
                                fullWidth
                              >
                                Pay from Wallet
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => step.action('external_payment')}
                                disabled={processing}
                                fullWidth
                              >
                                External Payment
                              </Button>
                            </>
                          )}
                          {step.completed && (
                            <Chip 
                              label="✓ Completed" 
                              color="success" 
                              variant="filled"
                              sx={{ alignSelf: 'center' }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {!step.completed && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Wallet Balance Payment:</strong> Instantly deducted from your {step.tokenType} balance.
                      <br />
                      <strong>External Payment:</strong> Requires manual verification by admin.
                    </Typography>
                  </Alert>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {currentStep === -1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              All requirements completed!
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You can now proceed with your withdrawal.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {currentStep === -1 && (
          <Button 
            onClick={() => { onComplete(); onClose(); }} 
            variant="contained"
            startIcon={<CheckIcon />}
          >
            Continue Withdrawal
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalVerificationModal; 